import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import LazyLoad from 'react-lazyload';
import AOS from 'aos';
import 'aos/dist/aos.css';
import BASE_URL from './apibaseurl/ApiBaseUrl';
import { toast } from 'react-toastify';



export default function ContactUs({ className = '' }) {
    const [active, setActive] = useState("sales");


    const [formData, setFormData] = useState({
        name: '',
        email1: '',
        email2: '',
        phone: '',
        country: '',
        message: '',
    });

    const [formErrors, setFormErrors] = useState({
        name: '',
        email1: '',
        phone: '',
        country: '',
        message: '',
    });


    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.id]: e.target.value }));
    };

    const validateForm = () => {
        const errors: any = {};

        if (!formData.name.trim()) errors.name = 'Full name is required';
        if (!formData.email1.trim()) errors.email1 = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email1)) errors.email1 = 'Email is invalid';

        if (!formData.phone.trim()) errors.phone = 'Phone number is required';

        if (active === 'sales' && !formData.country.trim()) errors.country = 'Country is required';
        if (!formData.message.trim()) errors.message = 'Message is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const form = new FormData();
        form.append('f_name', formData.name);
        form.append('email', formData.email1);
        form.append('phone', formData.phone);
        form.append('message', formData.message);
        form.append('country', formData.country);
        form.append('type', active === 'sales' ? '2' : '1'); // '2' for sales, '1' for technical

        try {
            const response = await fetch(`${BASE_URL}/save_contact_form`, {
                method: 'POST',
                body: form,
            });

            const result = await response.json();

            if (response.ok) {
                 toast.success(result.message || "Form submitted successfully!");
                setFormData({
                    name: '',
                    email1: '',
                    email2: '',
                    phone: '',
                    country: '',
                    message: '',
                });
            } else {
               toast.error(result.message || "Submission failed. Please try again.");
            }
        } catch (error) {
             toast.error("An error occurred. Please try again later.");
        }
    };



    useEffect(() => {
        AOS.init({
            duration: 800,
            once: true,
            offset: 100,
        });
    }, []);

    return (
        <div className="flex flex-col min-h-screen bg-white mx-auto max-w-7xl">
            {/* Top Section */}
            <section className="flex flex-col" data-aos="fade-up">
                <div className="flex-grow">
                    <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between px-6 py-20 gap-10">
                        <div className="max-w-xl space-y-5" data-aos="fade-right" data-aos-delay="100">
                            <h2 className="text-5xl font-bold text-[#234042] Bebas-neue">CONTACT US</h2>
                            <div className="flex items-center space-x-2 text-lg Bebas-neue">
                                <span className="text-[#234042]">HOME</span>
                                <span className="h-4 w-[1px] bg-gray-400"></span>
                                <span className="text-[#F47321] tracking-light">CONTACT US</span>
                            </div>
                            <p className="text-[#234042] text-base leading-relaxed helvetica-neue">
                                Have questions or need support? Our team at LiveQR is here to help—reach out anytime and we’ll get back to you as soon as possible.
                            </p>
                        </div>
                        <div className="relative" data-aos="zoom-in" data-aos-delay="200">
                            <LazyLoad height={400} offset={50} once>
                                <img
                                    src="/images/contactus.svg"
                                    alt="LiveQR process illustration"
                                    className="w-full max-w-md"
                                />
                            </LazyLoad>
                        </div>
                    </div>
                </div>
            </section>

            {/* Bottom Section */}
            <div className="flex bg-[#fafbfa] mb-20" data-aos="fade-up" data-aos-delay="100">
                {/* Sidebar */}
                <div className="w-[150px] lg:w-[280px] bg-white flex flex-col shrink-0">
                    {[
                        {
                            key: "sales",
                            label: "Sales Support",
                            icon: (
                                <LazyLoad height={24} offset={50} once>
                                    <img src="/images/SalesSupport.svg" alt="sales" className="w-6 h-6" />
                                </LazyLoad>
                            ),
                            isActive: active === "sales",
                        },
                        {
                            key: "technical",
                            label: "Technical Support",
                            icon: (
                                <LazyLoad height={24} offset={50} once>
                                    <img src="/images/contactlink.svg" alt="technical" className="w-6 h-6" />
                                </LazyLoad>
                            ),
                            isActive: active === "technical",
                        },

                    ].map((tab, index) => (
                        <button
                            key={tab.key}
                            onClick={() => setActive(tab.key)}
                            className={`relative flex items-center gap-2 px-4 py-3 text-left font-medium text-sm border-b w-full overflow-hidden transition-all duration-500
                                    ${tab.isActive
                                    ? "bg-[#F47321] text-white"
                                    : "bg-white text-[#F47321] border-[#F47321] shadow-lg hover:text-white before:absolute before:bottom-0 before:left-0 before:top-0 before:z-0 before:h-full before:w-0 before:bg-[#F47321] before:transition-all before:duration-500 hover:before:w-full"}`}
                        >
                            <span className="relative z-10 w-5 h-5">{tab.icon}</span>
                            <span className="relative z-10 truncate oswald text-sm lg:text-xl Bebas-neue">{tab.label}</span>
                        </button>
                    ))}
                </div>

                {/* Main Form */}
                <main className="flex-1">
                    <LazyLoad height={600} offset={50} once>
                        <div className="max-w-7xl mx-auto bg-gray-100 px-4 py-6 sm:px-8 sm:py-10" data-aos="fade-left" data-aos-delay="200">
                            <h1 className="text-2xl lg:text-[4rem] font-bold text-[#234042] mb-8 mt-10 Bebas-neue">
                                {active === "sales" ? "Sales Support" : "Technical Support"}
                            </h1>
                            <p className="text-gray-600 text-md lg:text-2xl mb-6">
                                Feel free to ask for details,
                                <span className="text-[#F47321] Bebas-neue"> don’t save any questions!</span>
                            </p>
                            <form
                                className="space-y-4 w-full"
                                onSubmit={handleSubmit}
                                data-aos="fade-up"
                                data-aos-delay="300"
                            >

                                <div className="flex flex-col sm:flex-row gap-4">
                                    <div className="w-full">
                                        <label htmlFor="name" className="block text-xs font-medium mb-1 helvetica-neue text-[#666666]">
                                            Full Name
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name ?? ""}
                                            onChange={handleChange}
                                            className="w-full border text-black helvetica-neue !text-sm border-slate-300 rounded-sm px-3 py-3  bg-white"
                                        />
                                        {formErrors.name && (
                                            <p className="text-red-500 !text-xs mt-1 helvetica-neue">{formErrors.name}</p>
                                        )}
                                    </div>
                                    <div className="w-full">
                                        <label htmlFor="email1" className="block text-xs font-medium mb-1 helvetica-neue text-[#666666]">
                                            Email Address
                                        </label>
                                        <input
                                            type="email"
                                            id="email1"
                                            name="email1"
                                            value={formData.email1 ?? ""}
                                            onChange={handleChange}
                                            className="w-full border border-slate-300 rounded-sm px-3 py-3 !text-sm text-black helvetica-neue bg-white"
                                        />
                                        {formErrors.email1 && (
                                            <p className="text-red-500 !text-xs mt-1 helvetica-neue">{formErrors.email1}</p>
                                        )}
                                    </div>
                                </div>


                                {active === "sales" ? (

                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <div className="w-full">
                                            <label htmlFor="country" className="block text-xs font-medium mb-1 helvetica-neue text-[#666666]">
                                                Country
                                            </label>
                                            <input
                                                type="text"
                                                id="country"
                                                name="country"
                                                value={formData.country ?? ""}
                                                onChange={handleChange}
                                                className="w-full border border-slate-300 rounded-sm px-3 py-3 !text-sm text-black helvetica-neue bg-white"
                                            />
                                            {formErrors.country && (
                                                <p className="text-red-500 !text-xs mt-1 helvetica-neue">{formErrors.country}</p>
                                            )}
                                        </div>
                                        <div className="w-full">
                                            <label htmlFor="phone" className="block text-xs font-medium mb-1 helvetica-neue text-[#666666]">
                                                Phone
                                            </label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                value={formData.phone ?? ""}
                                                onChange={handleChange}
                                                className="w-full border border-slate-300 rounded-sm px-3 py-3 !text-sm text-black helvetica-neue bg-white"
                                            />
                                            {formErrors.phone && (
                                                <p className="text-red-500 !text-xs mt-1 helvetica-neue">{formErrors.phone}</p>
                                            )}
                                        </div>
                                    </div>
                                ) : (

                                    <div className="w-full">
                                        <label htmlFor="phone" className="block text-xs font-medium mb-1 helvetica-neue text-[#666666]">
                                            Phone
                                        </label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={formData.phone ?? ""}
                                            onChange={handleChange}
                                            className="w-full border border-slate-300 rounded-sm px-3 py-3 !text-sm text-black helvetica-neue bg-white"
                                        />
                                        {formErrors.phone && (
                                            <p className="text-red-500 !text-xs mt-1 helvetica-neue">{formErrors.phone}</p>
                                        )}
                                    </div>
                                )}


                                <div>
                                    <label htmlFor="message" className="block text-xs font-medium mb-1 helvetica-neue text-[#666666]">
                                        Message
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        rows={4}
                                        value={formData.message ?? ""}
                                        onChange={handleChange}
                                        className="w-full border border-slate-300 rounded-sm px-3 py-3 !text-sm text-black helvetica-neue resize-none bg-white"
                                    />
                                    {formErrors.message && (
                                        <p className="text-red-500 !text-xs mt-1 helvetica-neue">{formErrors.message}</p>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="rounded-none w-full sm:w-auto bg-[#F47321] text-white hover:bg-[#e0621a] flex items-center justify-center gap-2 transition transform scale-y-125 text-xs lg:text-[18px] lg:px-4 lg:py-3 px-2 py-2 uppercase tracking-wide btn-hover-effect"
                                    data-aos="fade-up"
                                    data-aos-delay="400"
                                >
                                    <span>Submit</span>
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </form>

                        </div>
                    </LazyLoad>
                </main>
            </div>
        </div>
    );
}